<!-- Barcode Management -->
<div class="barcode-management-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fas fa-qrcode"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Table QR Codes</h1>
          <p class="page-description">Generate and manage QR codes for tables and ordering</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary btn-sm"
                (click)="generateMultiBarcodePrint(content2)"
                [disabled]="!hasSelectedBarcodes() || isLoadingBarcodes">
          <i class="fas fa-print"></i>
          Print Selected
        </button>
        <button class="btn btn-primary"
                (click)="generateBarcode(barcodeModal)"
                [disabled]="isLoadingPriceTags">
          @if(isLoadingPriceTags) {
            <i class="fas fa-spinner fa-spin"></i>
            Loading...
          } @else {
            <i class="fas fa-plus"></i>
            Generate Barcode
          }
        </button>
      </div>
    </div>
  </div>

  <!-- Filters Panel -->
  <div class="filters-panel">
    <div class="search-section">
      <div class="search-box">
        <div class="search-input-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input type="text"
                 class="search-input"
                 placeholder="Search tables, names, or UIDs..."
                 (input)="searchBarcodes($event)">
        </div>
      </div>
    </div>
    <div class="stats-section">
      @if(isLoadingBarcodes) {
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">Loading statistics...</div>
          </div>
        </div>
      } @else {
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fas fa-table"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ barcodesList?.length || 0 }}</div>
            <div class="stat-label">Total Tables</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-check-square"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ getSelectedCount() }}</div>
            <div class="stat-label">Selected</div>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Barcode Table -->
  <div class="data-panel">
    <div class="panel-header">
      <div class="panel-title">
        <div class="title-icon">
          <i class="fas fa-qrcode"></i>
        </div>
        <div>
          <div class="title-text">QR Code Tables</div>
          <div class="title-subtitle">Manage your table QR codes and configurations</div>
        </div>
      </div>
      <div class="panel-actions">
        <label class="d-flex align-items-center gap-2 cursor-pointer" style="font-size: 13px; color: var(--text-secondary); margin: 0;">
          <input type="checkbox"
                 class="form-check-input"
                 (change)="toggleSelectAll($event)"
                 style="margin: 0;">
          <span>Select All</span>
        </label>
      </div>
    </div>

    <div class="panel-body">
      @if(isLoadingBarcodes) {
        <div class="loading-state">
          <div class="loading-content">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
            </div>
            <div class="loading-text">
              <h5>Loading QR Code Tables</h5>
              <p>Please wait while we fetch your table data...</p>
            </div>
          </div>
        </div>
      } @else {
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th style="width: 50px;">Select</th>
                <th>Table Name</th>
                <th>Unique ID</th>
                <th>Price Tag</th>
                <th>Active Orders</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of filteredBarcodesList; let i = index"
                  [style.background]="item.selected ? 'var(--accent-subtle)' : ''">
                <td>
                  <input type="checkbox"
                         class="form-check-input"
                         [(ngModel)]="item.selected">
                </td>
                <td>
                  <div style="font-size: 13px; font-weight: 500; color: var(--text-primary);">{{ item.name }}</div>
                  <div style="font-size: 12px; color: var(--text-tertiary);">Table {{ i + 1 }}</div>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <code style="font-size: 12px; background: var(--bg-muted); padding: 4px 8px; border-radius: var(--radius-sm); color: var(--text-secondary);">{{ item.uid }}</code>
                    <button class="btn btn-outline-secondary btn-sm" (click)="copyToClipboard(item.uid)" style="padding: 2px 6px; font-size: 11px;">
                      <i class="fas fa-copy"></i>
                    </button>
                  </div>
                </td>
                <td>
                  @if(item.price_tag) {
                    <span class="badge badge-soft-success badge-dot">{{ item.price_tag.name }}</span>
                  } @else {
                    <span class="badge badge-soft-warning badge-dot">No tag assigned</span>
                  }
                </td>
                <td>
                  @if(item.active_orders_count > 0) {
                    <span class="badge badge-soft-info">
                      <i class="fas fa-shopping-cart me-1"></i>
                      {{ item.active_orders_count }}
                    </span>
                  } @else {
                    <span class="badge badge-soft-secondary">
                      <i class="fas fa-check-circle me-1"></i>
                      No active orders
                    </span>
                  }
                </td>
                <td>
                  <div class="d-flex gap-2">
                    <button class="btn btn-outline-secondary btn-sm"
                            (click)="viewQRCode(item)"
                            [disabled]="deletingItemId === item.id">
                      <i class="fas fa-qrcode"></i>
                      View QR
                    </button>
                    <button class="btn btn-danger btn-sm"
                            (click)="deleteBarcode(item.id)"
                            [disabled]="deletingItemId === item.id">
                      @if(deletingItemId === item.id) {
                        <i class="fas fa-spinner fa-spin"></i>
                        Deleting...
                      } @else {
                        <i class="fas fa-trash"></i>
                        Delete
                      }
                    </button>
                  </div>
                </td>
              </tr>

              @if(filteredBarcodesList.length === 0) {
                <tr>
                  <td colspan="6">
                    <div class="empty-state">
                      <div class="empty-icon">
                        <i class="fas fa-qrcode fa-3x"></i>
                      </div>
                      <div class="empty-content">
                        @if(searchTerm) {
                          <h5>No Tables Found</h5>
                          <p>No tables match your search criteria. Try adjusting your search.</p>
                        } @else {
                          <h5>No Tables Found</h5>
                          <p>Get started by generating your first QR code table.</p>
                          <button class="btn btn-primary" (click)="generateBarcode(barcodeModal)">
                            <i class="fas fa-plus me-1"></i>Generate First Barcode
                          </button>
                        }
                      </div>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</div>

<!-- Multi QR Print Modal -->
<ng-template #content2>
  <div class="modal-header">
    <div class="d-flex align-items-center gap-3">
      <div style="width: 36px; height: 36px; border-radius: var(--radius-md); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 15px;">
        <i class="fas fa-print"></i>
      </div>
      <div>
        <h4 class="modal-title" style="margin: 0;">Print QR Codes</h4>
        <p style="font-size: 12px; color: var(--text-tertiary); margin: 2px 0 0;">{{ selectedQRCodes.length }} table(s) selected for printing</p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeModal()"></button>
  </div>

  <div class="modal-body">
    @if(selectedQRCodes.length === 0) {
      <div class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-exclamation-circle fa-3x"></i>
        </div>
        <div class="empty-content">
          <h5>No QR Codes Selected</h5>
          <p>Please select at least one table to print QR codes.</p>
        </div>
      </div>
    } @else {
      <div class="barcode-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px;">
        <div *ngFor="let qr of selectedQRCodes"
             class="barcode-item"
             style="background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; text-align: center;">
          <h3 class="table-name" style="font-size: 15px; font-weight: 600; color: var(--text-primary); margin-bottom: 12px;">{{qr.name}}</h3>

          <div style="margin-bottom: 12px;">
            <div class="qr-wrapper" style="position: relative; display: inline-block;">
              <qrcode [qrdata]="qr.qrdata"
                      [width]="150"
                      [errorCorrectionLevel]="'M'"
                      [margin]="0"
                      [elementType]="'canvas'"></qrcode>
              <img src="icons/QR.png" class="qr-logo" alt="Logo"
                   style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 40px; height: 40px; background: white; padding: 3px; border-radius: 4px;">
            </div>
            <p style="font-size: 13px; font-weight: 500; color: var(--text-secondary); margin-top: 8px;">Scan to place order</p>
          </div>

          <div style="text-align: left; padding: 12px; background: var(--bg-muted); border-radius: var(--radius-md);">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; color: var(--text-secondary);">
              <i class="fas fa-qrcode" style="color: var(--accent); width: 14px;"></i>
              <span>Scan the QR Code with your phone</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; color: var(--text-secondary);">
              <i class="fas fa-list" style="color: var(--accent); width: 14px;"></i>
              <span>Browse and select menu items</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; color: var(--text-secondary);">
              <i class="fas fa-shopping-cart" style="color: var(--accent); width: 14px;"></i>
              <span>Review and place your order</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; color: var(--text-secondary);">
              <i class="fas fa-clock" style="color: var(--accent); width: 14px;"></i>
              <span>Track your order progress</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-secondary);">
              <i class="fas fa-bell" style="color: var(--accent); width: 14px;"></i>
              <span>Call a waiter for assistance</span>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <div class="modal-footer">
    <button class="btn btn-outline-secondary" (click)="closeModal()">
      <i class="fas fa-times me-1"></i>Cancel
    </button>
    <button class="btn btn-primary"
            (click)="printMultiQRSection()"
            [disabled]="selectedQRCodes.length === 0">
      <i class="fas fa-print me-1"></i>Print QR Codes
    </button>
  </div>
</ng-template>

<!-- Generate Barcode Modal -->
<ng-template #barcodeModal>
  <div class="modal-header">
    <div class="d-flex align-items-center gap-3">
      <div style="width: 36px; height: 36px; border-radius: var(--radius-md); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 15px;">
        <i class="fas fa-qrcode"></i>
      </div>
      <div>
        <h4 class="modal-title" style="margin: 0;">Generate New Barcode</h4>
        <p style="font-size: 12px; color: var(--text-tertiary); margin: 2px 0 0;">Create a QR code for a new table</p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeModal()"></button>
  </div>

  <div class="modal-body">
    <form [formGroup]="generateForm" (ngSubmit)="onsubmit()">
      <div class="form-group">
        <label class="form-label">
          <i class="fas fa-tag me-1"></i>
          Table Name <span style="color: var(--danger-text);">*</span>
        </label>
        <input type="text"
               class="form-control"
               formControlName="name"
               placeholder="Enter table name (e.g., Table 1, Window Booth, etc.)">
        <small style="color: var(--text-tertiary); margin-top: 4px; display: block;">Choose a descriptive name for easy identification</small>
      </div>

      <div class="form-group">
        <label class="form-label">
          <i class="fas fa-price-tag me-1"></i>
          Price Tag <span style="color: var(--danger-text);">*</span>
        </label>
        @if(isLoadingPriceTags) {
          <div class="form-control d-flex align-items-center" style="color: var(--text-tertiary);">
            <i class="fas fa-spinner fa-spin me-2"></i>
            Loading price tags...
          </div>
        } @else {
          <select class="form-control" formControlName="price_tag_id">
            <option value="">Select a price tag</option>
            @for (tag of priceTags; track tag.id) {
              <option [value]="tag.id">{{ tag.name }}</option>
            }
          </select>
        }
        <small style="color: var(--text-tertiary); margin-top: 4px; display: block;">Associate this table with a pricing category</small>
      </div>

      <div class="modal-footer" style="padding: 16px 0 0; border-top: none;">
        <button type="button"
                class="btn btn-outline-secondary"
                (click)="closeModal()">
          Cancel
        </button>
        <button type="submit"
                class="btn btn-primary"
                [disabled]="isLoading || !generateForm.valid">
          <span *ngIf="isLoading">
            <i class="fas fa-spinner fa-spin me-1"></i>Generating...
          </span>
          <span *ngIf="!isLoading">
            <i class="fas fa-plus me-1"></i>Generate Barcode
          </span>
        </button>
      </div>
    </form>
  </div>
</ng-template>
